import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { useTranslation } from 'react-i18next'
import { Chat } from '@/models.ts'
import dayjs from 'dayjs'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import useGlobalStore from '@/useGlobalStore.ts'

interface PageSideDrawerProps {
	isLoading: boolean
	chatHistoryList: Chat[]
	onChatSelected: (id: number) => void
	isInDialog?: boolean
}

const PageSideDrawer = ({ isLoading, chatHistoryList, onChatSelected, isInDialog = false }: PageSideDrawerProps) => {
	const { t } = useTranslation()
	const { appLanguageCode, availableAppLanguages, activeChat } = useGlobalStore()

	// If isInDialog is true, map to Drawer components. Otherwise, map to divs/HTML elements.
	const Container = isInDialog ? DrawerContent : 'div'
	const Header = isInDialog ? DrawerHeader : 'div'
	const Title = isInDialog ? DrawerTitle : 'h2'
	const Description = isInDialog ? DrawerDescription : 'p'

	return (
		<Container
			dir={availableAppLanguages[appLanguageCode]?.dir}
			className={`flex flex-col h-screen top-0 m-0 bg-background ${isInDialog ? 'w-[80%]' : 'w-full bg-card'} rounded-t-none border-0 pt-safe`}>
			<Header className={!isInDialog ? 'grid gap-1.5 p-4 px-2.5' : ''}>
				<Title className="text-lg font-semibold leading-none tracking-tight">{t('History')}</Title>
				<Description className="text-sm text-muted-foreground">{t('View your chat history here')}</Description>
			</Header>
			<Separator />
			<div className="flex-1 break-all overflow-y-auto [touch-action:pan-y]">
				{isLoading && chatHistoryList.length <= 0 ? (
					<Skeleton className="m-2 rounded-xl" style={{ height: '100%' }} />
				) : (
					chatHistoryList
						.filter((x) => x.title !== null && x.title.length > 0)
						.map((x) => (
							<div
								key={x.id}
								onClick={() => onChatSelected(x.id!)}
								className={`py-2 px-2.5 md:px-3 select-none cursor-pointer transition-colors dark:active:bg-muted/50 active:bg-gray-300 ${activeChat?.id === x.id ? 'dark:bg-muted/50 bg-gray-300' : ''}`}>
								<div className="font-semibold break-keep">{x.title}</div>
								<small>{dayjs().to(dayjs.utc(x.created_at).local())}</small>
							</div>
						))
				)}
			</div>
		</Container>
	)
}

export default PageSideDrawer
